import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Select, Spin } from 'antd';
import { CheckCircleFilled, ExclamationCircleFilled, SlackOutlined } from '@ant-design/icons';
import { AxiosError } from 'axios';
import taskonioMark from '../../assets/images/logo/favicon.svg';
import { setCurrentlyActiveOrganization } from '../../common/auth';
import { useConfirmSlackInstallation, useGetPendingSlackInstallation } from '../../serverState/slack';
import { useGetOrganizations } from '../../serverState/organization';
import RoutePaths from '../../router/RoutePaths';
import { describeSlackInstallError } from '../../common/slackCallbackResult';

/**
 * Where Slack's install callback lands.
 *
 * SECURITY: this screen is a control, not a formality. The unauthenticated callback
 * verifies the Slack OAuth code and persists nothing - it parks the result under a
 * single-use Redis token, because it cannot know which Taskonio organization the caller
 * meant. Anyone holding that token can hand it to someone else; if this screen
 * auto-confirmed from URL parameters, a link alone (`?pending=<attacker token>`) would
 * let an attacker bind their own Slack workspace into any organization its recipient
 * administers.
 *
 * Three rules follow, and none of them may be relaxed:
 *   1. NEVER confirm without a deliberate human click.
 *   2. ALWAYS display what is being bound (the Slack workspace name) before binding it,
 *      via the non-destructive GET /pending-installation.
 *   3. The organization is chosen by the human HERE and sent as a route param to the
 *      ADMIN-guarded confirm endpoint. The server does not accept an organization from
 *      anywhere else.
 *
 * DESIGN: this is a consent screen reached from outside the app, so it follows the same
 * shape as `pages/GithubSetupComplete/GithubSetupComplete.tsx` - the app's other install
 * consent screen - rather than the in-app Settings shell: centred card, brand mark,
 * full-width rounded actions. Actions are antd `Button`/`Select`, not `BaseButton`:
 * `BaseButton` renders a `<div>`, and a keyboard-only user landing here from Slack could
 * not otherwise complete the install at all.
 */
export default function SlackSetupComplete() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingToken = searchParams.get('pending');
  const setupError = searchParams.get('error');

  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  // A 410 means the pending token is gone and nothing on this screen can bring it back,
  // so it forces the same terminal "expired" card the initial peek-failure path below
  // uses. A 409 is different: the server peeks, runs its one-workspace-per-organization
  // check, and only spends the token once a bind is actually going to happen, so the
  // token is still there and the retry this screen leaves live is a real offer.
  const [tokenDead, setTokenDead] = useState(false);

  const pending = useGetPendingSlackInstallation(setupError ? null : pendingToken);
  const confirmMutation = useConfirmSlackInstallation();
  const orgs = useGetOrganizations();

  // With exactly one administered organization there is nothing to choose, so the screen
  // states the binding instead of offering a picker with a single row.
  const onlyOrg = orgs.data?.length === 1 ? orgs.data[0] : undefined;

  useEffect(() => {
    if (onlyOrg) setSelectedOrgId(onlyOrg.id);
  }, [onlyOrg]);

  const selectedOrg = orgs.data?.find((org) => org.id === selectedOrgId);

  const card = (children: React.ReactNode) => (
    <div className="flex min-h-screen justify-center bg-white px-4 py-16">
      <div className="h-fit w-full max-w-md rounded-xl border border-solid border-neutral-300 p-8">{children}</div>
    </div>
  );

  /** Slack ⇄ Taskonio, so the screen says what it is before a word is read. */
  const connectorHeader = (linked: boolean) => (
    <div className="flex items-center justify-center gap-2">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-solid border-neutral-300 bg-brand-grey-light">
        <SlackOutlined className="text-2xl text-brand-grey-dark" />
      </span>
      <span className="h-px w-5 bg-neutral-400" />
      {linked ? (
        <CheckCircleFilled className="text-base text-brand-taskonio-green" />
      ) : (
        <ExclamationCircleFilled className="text-base text-neutral-700" />
      )}
      <span className="h-px w-5 bg-neutral-400" />
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-solid border-neutral-300 bg-brand-grey-light">
        <img src={taskonioMark} alt="" className="h-7 w-7" />
      </span>
    </div>
  );

  /** Every terminal state renders through here so they share one silhouette. */
  const statusCard = (title: string, body: React.ReactNode, action: React.ReactNode) =>
    card(
      <>
        {connectorHeader(false)}
        <h1 className="m-0 mt-6 text-center text-xl font-semibold leading-7 text-brand-grey-dark">{title}</h1>
        <p className="m-0 mt-3 text-center text-sm leading-relaxed text-neutral-900">{body}</p>
        <div className="mt-8">{action}</div>
      </>
    );

  const backHome = (
    <Button size="large" shape="round" block onClick={() => navigate(RoutePaths.Home)}>
      Back to Taskonio
    </Button>
  );

  if (setupError) {
    // Each code says something different about what to do next - an expired state is
    // retryable from Integrations, a failed exchange is worth simply trying again - so
    // they are interpreted rather than flattened into one sentence.
    return statusCard("Couldn't connect Slack", describeSlackInstallError(setupError), backHome);
  }

  if (!pendingToken) {
    return statusCard(
      'Nothing to confirm',
      'This link is missing the token Slack setup expects to send back. Start again from Settings → Integrations.',
      backHome
    );
  }

  if (pending.isLoading || orgs.isLoading) {
    return card(
      <div className="flex flex-col items-center gap-4 py-6">
        <Spin size="large" />
        <p className="m-0 text-sm text-neutral-900">Verifying your Slack workspace…</p>
      </div>
    );
  }

  if (orgs.isError) {
    return statusCard(
      "Couldn't load your organizations",
      'Something went wrong while checking which organizations you administer. Please refresh and try again.',
      backHome
    );
  }

  if (pending.isError || !pending.data || tokenDead) {
    // No specific reason named: a 404 here covers an unknown, expired, or already-spent
    // token alike, and this copy must not claim to know which.
    return statusCard(
      "This link can't be used",
      'This setup link has expired or is no longer valid. Start again from Settings → Integrations.',
      backHome
    );
  }

  const onConfirm = async () => {
    if (!selectedOrgId) return;
    setConflict(null);
    try {
      await confirmMutation.mutateAsync({ organizationId: selectedOrgId, pendingToken });
      const org = orgs.data?.find((o) => o.id === selectedOrgId);
      if (org) {
        // The settings routes read the active organization from localforage, not from
        // the URL's :orgId - without this, confirming into a non-active organization
        // lands on the right URL but reads the wrong org's data.
        await setCurrentlyActiveOrganization(org);
        navigate(`/${org.publicId}/settings/integrations`);
        window.location.reload();
        return;
      }
      navigate(RoutePaths.Home);
    } catch (err) {
      const status = (err as AxiosError)?.response?.status;
      if (status === 409) {
        setConflict('That Slack workspace is already connected to another organization.');
        return;
      }
      if (status === 410) {
        setTokenDead(true);
        return;
      }
      setConflict('Something went wrong connecting this workspace. Please try again.');
    }
  };

  const detailRow = (label: string, value: React.ReactNode) => (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="m-0 shrink-0 text-sm text-neutral-900">{label}</dt>
      <dd className="m-0 min-w-0 truncate text-sm font-medium text-brand-grey-dark">{value}</dd>
    </div>
  );

  // Names the destination organization, not the workspace: the workspace name is
  // already shown above in the detail row, and repeating it here would make the
  // workspace name ambiguous for a screen reader ("is that the same one, or a second
  // match?") as well as for `findByText` queries that look for it once.
  const connectLabel = selectedOrg ? `Connect to ${selectedOrg.name}` : 'Connect';

  return card(
    <>
      {connectorHeader(true)}

      <h1 className="m-0 mt-6 text-center text-xl font-semibold leading-7 text-brand-grey-dark">Connect Slack</h1>
      <p className="m-0 mt-2 text-center text-sm leading-relaxed text-neutral-900">
        {onlyOrg
          ? 'We verified this Slack workspace. Confirm the Taskonio organization it should belong to.'
          : 'We verified this Slack workspace. Choose the Taskonio organization it should belong to.'}
      </p>

      {/* Rule 2: what is being bound, shown before it is bound. */}
      <dl className="m-0 mt-6 flex flex-col gap-3 rounded-lg bg-brand-grey-light p-4">
        {detailRow('Slack workspace', pending.data.teamName)}
      </dl>

      {!orgs.data || orgs.data.length === 0 ? (
        <Alert
          className="mt-6"
          type="warning"
          showIcon={true}
          message="You need to be an admin of a Taskonio organization to connect a Slack workspace."
        />
      ) : onlyOrg ? (
        // Nothing to pick, so the checkmark is honest here: this is a statement of what
        // will be bound, and the Connect button below is still the deliberate act.
        <div className="mt-6">
          <p className="m-0 mb-2 text-sm font-medium text-brand-grey-dark">Taskonio organization</p>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-solid border-brand-taskonio-green bg-taskonioGreen-100 px-3 py-2.5">
            <span className="min-w-0 truncate text-sm text-brand-grey-dark">{onlyOrg.name}</span>
            <CheckCircleFilled className="shrink-0 text-brand-taskonio-green" />
          </div>
        </div>
      ) : (
        <div className="mt-6">
          <p className="m-0 mb-2 text-sm font-medium text-brand-grey-dark">Connect it to which organization?</p>
          <Select
            className="w-full"
            size="large"
            value={selectedOrgId ?? undefined}
            placeholder="Choose an organization"
            onChange={(value: number) => setSelectedOrgId(value)}
            options={orgs.data.map((org) => ({ value: org.id, label: org.name }))}
          />
        </div>
      )}

      {conflict && <Alert className="mt-4" type="error" showIcon={true} message={conflict} />}

      <div className="mt-8 flex flex-col gap-3">
        <Button
          type="primary"
          size="large"
          shape="round"
          block
          disabled={!selectedOrgId}
          loading={confirmMutation.isPending}
          onClick={() => void onConfirm()}
        >
          {connectLabel}
        </Button>
        <Button size="large" shape="round" block onClick={() => navigate(RoutePaths.Home)}>
          Cancel
        </Button>
      </div>
    </>
  );
}
